import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, retry, switchMap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';

import { ExplorerServiceConnectorService } from '@finder/shared/services/explorer-svc-connector/explorer-svc-connector.service';
import { AVAILABILITY_CALENDAR } from './availability-calendar.constants';
import { GetDateObjectProvider, GET_DATE_OBJECT } from '@finder/shared/utils/date/getDateObject';
import {
    GetSimpleDateFromDateObjectProvider,
    GET_SIMPLE_DATE_FROM_DATE_OBJECT
} from '@finder/shared/utils/date/getSimpleDateFromDateObject';

@Injectable()
export class AvailabilityCalendarService {

    constructor(
        private explorerService: ExplorerServiceConnectorService,
        @Inject(GET_DATE_OBJECT) private getDateObject: GetDateObjectProvider,
        @Inject(GET_SIMPLE_DATE_FROM_DATE_OBJECT) private getSimpleDateFromDateObject: GetSimpleDateFromDateObjectProvider
    ) { }

    getMonthlyAvailability(
        siteId: string,
        entityId: string,
        date: string,
        numOfMonths: number,
        roomAvailability: boolean,
        locale: string
    ): Observable<any> {
        // Create an object we can alter later on
        const retryObject = { numOfMonths };

        const updateRetryObj = () => {
            /**
             * Move the number of months forward three. Desktop displays 3 months at a time
             */
            retryObject.numOfMonths += AVAILABILITY_CALENDAR.REQUEST_NUMBER_OF_MONTHS;
        };

        // Adjust the date back three days to make sure there's an overlap when a sailing begins and ends on different months
        const adjustedDate = this.getDateObject(date);
        adjustedDate.setDate(-3);
        const adjustedDateStr = this.getSimpleDateFromDateObject(adjustedDate, locale);

        /**
         * Return an observable that will be either the availability or an error
         * saying it couldn't find any availability
         */
        return of(retryObject)
            .pipe(
                // Directly pass roomAvailability through. It will always have a value that lets us know if we should check for rooms
                switchMap(obj => this.explorerService.getMonthlyAvailability(
                    siteId,
                    entityId,
                    adjustedDateStr,
                    obj.numOfMonths,
                    roomAvailability
                )),
                map((result: any) => {
                    // Check to see if the result is empty
                    if (!result || isEmpty(result.availableReservations)) {
                        updateRetryObj();

                        // Throw an error to try again
                        throw new Error(
                            `Missing available reservations data for ${entityId}. ` +
                            `Trying ${AVAILABILITY_CALENDAR.REQUEST_NUMBER_OF_MONTHS} months out.`
                        );
                    }

                    return result;
                }),
                catchError(err => {
                    updateRetryObj();

                    // Unable to reach the end point due to a bad entityId. Just stop
                    throw new Error(`Unable to reach the ${entityId} end point for availability data.`);
                }),
                /**
                 * Try eight  times before stoping
                 * This allows us to look for up to two years
                 * 24 / 8 = 3
                 * We search in 3 month blocks
                 */
                retry(8),
            );
    }

    getPlaceholderAvailability(
        siteId: string,
        startDate: string,
        endDate: string,
        requestedDate: string,
        numOfMonths: number,
        excludedDates?: string,
        sailingDates?: string
    ): Observable<any> {
        return this.explorerService.getPlaceholderAvailability(
            siteId,
            startDate,
            endDate,
            requestedDate,
            numOfMonths,
            excludedDates,
            sailingDates
        );
    }
}
